import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const SCOPES = [
  ['query:read', 'Ask Qflow and receive grounded resolutions'],
  ['floor:read', 'Read permitted Floor Space discussions'],
  ['floor:write', 'Create and reply to Floor Space discussions'],
];

const IntegrationAccessManager = ({ integration, capabilities, users, currentUser, onClose }) => {
  const apiMode = integration.type === 'qflow_api';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'master_admin' || currentUser?.is_master_admin;
  const [records, setRecords] = useState([]);
  const [oneTimeKey, setOneTimeKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(apiMode
    ? { name: '', user_id: '', scopes: ['query:read'] }
    : { external_user_id: '', user_id: '', display_name: '' });
  const endpoint = `/admin/integrations/${integration.id}/${apiMode ? 'credentials' : 'identity-mappings'}`;

  const load = useCallback(async () => {
    try {
      const response = await axiosInstance.get(endpoint);
      setRecords(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load integration access');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    try {
      const response = await axiosInstance.post(endpoint, form);
      if (apiMode) {
        setOneTimeKey(response.data.api_key);
        setForm({ name: '', user_id: '', scopes: ['query:read'] });
      } else {
        setForm({ external_user_id: '', user_id: '', display_name: '' });
      }
      toast.success(apiMode ? 'API key created' : 'Teams identity mapped');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save integration access');
    }
  };

  const remove = async (record) => {
    const suffix = apiMode ? `credentials/${record.id}` : `identity-mappings/${record.id}`;
    try {
      await axiosInstance.delete(`/admin/integrations/${integration.id}/${suffix}`);
      toast.success(apiMode ? 'API key revoked' : 'Teams identity mapping removed');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove integration access');
    }
  };

  const toggleScope = (scope) => {
    const scopes = new Set(form.scopes || []);
    if (scopes.has(scope)) scopes.delete(scope); else scopes.add(scope);
    setForm({ ...form, scopes: [...scopes] });
  };
  const runtime = capabilities?.teams || {};
  const api = capabilities?.generic_api || {};

  return <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{apiMode ? 'Generic Qflow API keys' : 'Microsoft Teams user mappings'}</DialogTitle>
        <DialogDescription>{apiMode ? 'Each key is bound to one Qflow user and selected scopes.' : 'Map each Microsoft Entra user object ID to an active Qflow user. Qflow permissions remain authoritative.'}</DialogDescription>
      </DialogHeader>

      {apiMode ? <div className="rounded-lg border bg-slate-50 p-3 text-sm">
        <p className="font-medium">Endpoints</p>
        <p className="mt-1 break-all text-slate-600">Query: {api.query_endpoint || 'Available after deployment'}</p>
        <p className="break-all text-slate-600">Floor Space: {api.floor_question_endpoint || 'Available after deployment'}</p>
        <p className="mt-2 text-xs text-slate-500">Send the one-time key as the X-Qflow-Key request header.</p>
      </div> : <div className={`rounded-lg border p-3 text-sm ${runtime.runtime_configured ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <p className="font-medium">{runtime.runtime_configured ? 'Teams runtime configured' : 'Teams runtime setup required'}</p>
        <p className="mt-1 break-all">Messaging endpoint: {runtime.messaging_endpoint || 'Available after deployment'}</p>
        <p className="mt-1 text-xs">A Microsoft 365 administrator must install and consent to the Qflow Teams app before messages can arrive.</p>
      </div>}

      {oneTimeKey && <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="font-semibold text-red-900">Copy this key now—it will not be shown again.</p>
        <div className="mt-2 flex gap-2"><Input readOnly value={oneTimeKey} className="font-mono text-xs" /><Button type="button" onClick={() => navigator.clipboard.writeText(oneTimeKey)}>Copy</Button></div>
      </div>}

      {canEdit && <form onSubmit={submit} className="space-y-4 rounded-lg border p-4">
        <h3 className="font-semibold">{apiMode ? 'Create a scoped API key' : 'Map a Teams user'}</h3>
        {apiMode ? <div><Label>Key name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="HR assistant integration" required /></div> : <>
          <div><Label>Microsoft Entra user object ID</Label><Input value={form.external_user_id} onChange={(e) => setForm({ ...form, external_user_id: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" required /></div>
          <div><Label>Display name (optional)</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></div>
        </>}
        <div><Label>Qflow user</Label><select className="mt-1 h-10 w-full rounded-md border px-3" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required>
          <option value="">Select a Qflow user</option>
          {users.map((user) => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}
        </select></div>
        {apiMode && <div className="space-y-2"><Label>Allowed scopes</Label>{SCOPES.map(([scope, description]) => <label key={scope} className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={(form.scopes || []).includes(scope)} onChange={() => toggleScope(scope)} />
          <span><span className="font-medium">{scope}</span><span className="block text-slate-500">{description}</span></span>
        </label>)}</div>}
        <Button type="submit">{apiMode ? 'Create API key' : 'Add user mapping'}</Button>
      </form>}

      <div className="space-y-2">
        <h3 className="font-semibold">{apiMode ? 'Keys' : 'Mapped users'}</h3>
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && !records.length && <p className="rounded-lg border border-dashed p-4 text-sm text-slate-500">None configured yet.</p>}
        {records.map((record) => <div key={record.id} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
          <div><p className="font-medium">{apiMode ? record.name : (record.display_name || record.user_name)}</p>
            <p className="text-slate-500">{apiMode ? `${(record.scopes || []).join(', ')} · ${record.revoked_at ? 'Revoked' : 'Active'}` : `${record.external_user_id} → ${record.user_name}`}</p></div>
          {canEdit && !record.revoked_at && <Button type="button" variant="outline" className="text-red-600" onClick={() => remove(record)}>{apiMode ? 'Revoke' : 'Remove'}</Button>}
        </div>)}
      </div>
      <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Close</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
};

export default IntegrationAccessManager;
